import { type FC, type ReactNode, useCallback, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';

import clsx from 'clsx';
import isEqual from 'lodash/isEqual';

import { Chart } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/Chart';
import { ChartsGroup } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/RevenueChart/components/ChartsGroup';
import {
  getDefaultRevenueChartFilterValues,
  RevenueChartFilter,
  type RevenueChartFilter as RevenueChartFilterValues,
} from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/RevenueChart/components/RevenueChartFilter';
import {
  type RevenueChartData,
  useRevenueChartData,
} from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/hooks/use-revenue-chart-data';
import { getEmptyXAxisValues } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/utils/empty-chart';
import type { GateGroupDetails } from '@/app/[locale]/(authorized)/payment-gates/[id]/components/PaymentGate/hooks/use-gate-group-details';
import { type WithClassName } from '@/types/react';
import { isDefined } from '@/utils/utils';

import { RevenueChartOperationType } from '../../../../../../../../../../../generated/graphql';

import styles from './RevenueChart.module.scss';

type RevenueChartProps = WithClassName & {
  title: ReactNode;
  defaultCurrency: {
    id: string;
    name: string;
  };
  gateGroupDetails?: GateGroupDetails;
};

export const RevenueChart: FC<RevenueChartProps> = (props) => {
  const { className, title, defaultCurrency, gateGroupDetails } = props;

  const [filter, setFilter] = useState<RevenueChartFilterValues | undefined>(() => ({
    ...getDefaultRevenueChartFilterValues(),
    currency: defaultCurrency,
  }));

  const {
    chartsData,
    loading,
    refetch: refetchData,
    groupBy,
  } = useRevenueChartData({
    filter,
    gateGroupId: gateGroupDetails?.id,
  });

  const emptyChartData = useMemo((): RevenueChartData => {
    const emptyDates = getEmptyXAxisValues();

    const getCategoryData = () => ({
      main_value: 0,
      percent_change: 0,
      y_axis_divisions: [0, 0, 0, 0, 0],
      current_period_start: '',
      current_period_end: '',
      comparison_period_start: null,
      comparison_period_end: null,
      comparison_period_label: null,
      data: emptyDates.map((date) => ({
        date,
        value: 0,
        comparison_value: null,
        comparison_date: null,
      })),
    });

    return {
      revenue: getCategoryData(),
      average_bill: getCategoryData(),
      return: getCategoryData(),
      amount: getCategoryData(),
    };
  }, []);

  const defaultFormValues = useMemo(() => {
    if (!gateGroupDetails) {
      return undefined;
    }

    return {
      currency: defaultCurrency,
    };
  }, [defaultCurrency, gateGroupDetails]);

  const onFilter = useCallback<SubmitHandler<RevenueChartFilterValues>>(
    (data) => {
      setFilter(data);

      // Если ничего не поменялось, аполло сам не перезапустит query
      if (isEqual(data, filter)) {
        refetchData();
      }
    },
    [filter, refetchData]
  );

  return (
    <Chart
      className={clsx(styles.root, className)}
      chartClassName={styles.chart}
      title={title}
      filter={
        <RevenueChartFilter
          className={styles.filter}
          defaultValues={filter ?? defaultFormValues}
          isInGate={isDefined(gateGroupDetails)}
          onSubmit={onFilter}
        />
      }
      chart={
        <ChartsGroup
          loading={loading}
          chartsData={chartsData ?? emptyChartData}
          groupBy={groupBy}
          operationType={filter?.operationType ?? RevenueChartOperationType.Invoice}
          currency={filter?.currency.id ?? defaultCurrency.id}
          withComparisonPlaceholder={filter?.compareEnabled ?? false}
        />
      }
    />
  );
};
